import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let sisFieldId = 0;
type SisFieldState = '' | 'ok' | 'err';

@Component({
  selector: 'app-sis-field',
  templateUrl: './sis-field.component.html',
  styleUrls: ['./sis-field.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SisFieldComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SisFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() type = 'text';
  @Input() placeholder = '';
  @Input() helper = '';
  @Input() error = '';
  @Input() state: SisFieldState = '';
  @Input() icon: 'check' | '' = '';
  @Input() readonly = false;
  @Input() loading = false;
  @Input() loadedKey: unknown = null;
  @Input() loadedMessage = '';
  @Input() loadFailed = false;
  @Input() showRetry = true;
  @Output() retryLoad = new EventEmitter<void>();
  @Input() displayValue: string | null = null;
  @Input() min: string | number | null = null;
  @Input() max: string | number | null = null;
  @Input() maxlength: string | number | null = null;

  readonly inputId = `sis-field-${++sisFieldId}`;
  value = '';
  @Input() disabled = false;
  // Visual + interaction lock that is independent of the reactive-form disabled
  // state. Unlike `disabled` (driven by ControlValueAccessor.setDisabledState,
  // which the form resets to match the control), `locked` is never overwritten,
  // so it can lock a field bound with formControlName while keeping its value in
  // the form (getRawValue still includes it).
  @Input() locked = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get describedBy(): string | null {
    const ids = [];
    if (this.helper || this.loadFailed) {
      ids.push(`${this.inputId}-helper`);
    }
    if (this.error) {
      ids.push(`${this.inputId}-error`);
    }
    return ids.length ? ids.join(' ') : null;
  }

  get visualState(): SisFieldState {
    return this.error ? 'err' : this.state;
  }

  get helperText(): string {
    return this.error || this.helper;
  }

  get failedText(): string {
    return this.error || this.helper || 'No se pudo cargar';
  }

  get effectiveDisabled(): boolean {
    return this.disabled || this.loading;
  }

  // Native disabled / greyed visual: either the form-driven disabled state or the
  // form-independent lock.
  get isLockedOrDisabled(): boolean {
    return this.effectiveDisabled || this.locked;
  }

  get inputValue(): string {
    return this.displayValue == null ? this.value : this.displayValue;
  }

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
  }
}
